/**
 * D&D 5e Condition → Mechanical Effects mapping.
 * Extracted from shared/EffectResolver.ts for system isolation.
 */
/** Standard D&D 5e condition → mechanical effects mapping. */
export const DND5E_CONDITION_EFFECTS = {
    Blinded: [
        { target: 'attack_rolls', mode: 'disadvantage', value: 0 },
    ],
    Charmed: [],
    Deafened: [],
    Frightened: [
        { target: 'ability_checks', mode: 'disadvantage', value: 0 },
        { target: 'attack_rolls', mode: 'disadvantage', value: 0 },
    ],
    Grappled: [
        { target: 'speed', mode: 'override', value: 0 },
    ],
    Incapacitated: [],
    Invisible: [
        { target: 'attack_rolls', mode: 'advantage', value: 0 },
    ],
    Paralyzed: [
        { target: 'str_save', mode: 'auto-fail', value: 0 },
        { target: 'dex_save', mode: 'auto-fail', value: 0 },
    ],
    Petrified: [
        { target: 'str_save', mode: 'auto-fail', value: 0 },
        { target: 'dex_save', mode: 'auto-fail', value: 0 },
    ],
    Poisoned: [
        { target: 'attack_rolls', mode: 'disadvantage', value: 0 },
        { target: 'ability_checks', mode: 'disadvantage', value: 0 },
    ],
    Prone: [
        { target: 'attack_rolls', mode: 'disadvantage', value: 0 },
    ],
    Restrained: [
        { target: 'speed', mode: 'override', value: 0 },
        { target: 'attack_rolls', mode: 'disadvantage', value: 0 },
        { target: 'dex_save', mode: 'disadvantage', value: 0 },
    ],
    Stunned: [
        { target: 'str_save', mode: 'auto-fail', value: 0 },
        { target: 'dex_save', mode: 'auto-fail', value: 0 },
    ],
    Unconscious: [
        { target: 'str_save', mode: 'auto-fail', value: 0 },
        { target: 'dex_save', mode: 'auto-fail', value: 0 },
    ],
    Dead: [
        { target: 'str_save', mode: 'auto-fail', value: 0 },
        { target: 'dex_save', mode: 'auto-fail', value: 0 },
    ],
};
// Canonical condition keys — the Capitalized-English keys of
// DND5E_CONDITION_EFFECTS above. Captured BEFORE the Russian aliases are
// merged into the same object below.
const CANONICAL_CONDITION_KEYS = Object.keys(DND5E_CONDITION_EFFECTS);
/** Russian condition name → English mapping. Includes the label variants the
 * HUD historically used ('Окаменел'/'Лежит'/'Опутан') alongside the original
 * aliases, so stored data from either era normalizes to the same canon. */
const ruToEn = {
    'Ослеплён': 'Blinded',
    'Очарован': 'Charmed',
    'Оглушён': 'Deafened',
    'Испуган': 'Frightened',
    'Схвачен': 'Grappled',
    'Недееспособен': 'Incapacitated',
    'Невидим': 'Invisible',
    'Парализован': 'Paralyzed',
    'Окаменён': 'Petrified',
    'Окаменел': 'Petrified',
    'Отравлен': 'Poisoned',
    'Распластан': 'Prone',
    'Лежит': 'Prone',
    'Сдержан': 'Restrained',
    'Опутан': 'Restrained',
    'Ошеломлён': 'Stunned',
    'Без сознания': 'Unconscious',
    'Мёртв': 'Dead',
};
/** Canonical key → preferred Russian display label (the HUD-era variants). */
export const DND5E_CONDITION_LABELS_RU = {
    Blinded: 'Ослеплён',
    Charmed: 'Очарован',
    Deafened: 'Оглушён',
    Frightened: 'Испуган',
    Grappled: 'Схвачен',
    Incapacitated: 'Недееспособен',
    Invisible: 'Невидим',
    Paralyzed: 'Парализован',
    Petrified: 'Окаменел',
    Poisoned: 'Отравлен',
    Prone: 'Лежит',
    Restrained: 'Опутан',
    Stunned: 'Ошеломлён',
    Unconscious: 'Без сознания',
    Dead: 'Мёртв',
};
// Add Russian aliases to DND5E_CONDITION_EFFECTS so resolveEffects can look up
// conditions by Russian name directly without a separate normalization step.
for (const [ru, en] of Object.entries(ruToEn)) {
    if (DND5E_CONDITION_EFFECTS[en]) {
        DND5E_CONDITION_EFFECTS[ru] = DND5E_CONDITION_EFFECTS[en];
    }
}
// lowercase → canonical (covers the token ConditionsDialog dialect, which
// stores lowercase ids like 'blinded'/'unconscious').
const lcToCanonical = {};
for (const key of CANONICAL_CONDITION_KEYS) {
    lcToCanonical[key.toLowerCase()] = key;
}
/**
 * Normalize a condition name to the canonical Capitalized-English key.
 * Handles all three historical dialects: Russian HUD labels ('Отравлен'),
 * lowercase token-dialog ids ('poisoned'), and the canon itself ('Poisoned').
 * Unknown names (e.g. 'exhaustion', custom strings) pass through unchanged.
 */
export function normalizeConditionName(name) {
    return ruToEn[name] ?? lcToCanonical[name.toLowerCase()] ?? name;
}
/**
 * Canonical conditions that changed between two lists (added ∪ removed).
 * Both inputs are normalized first, so mixed dialects (token lowercase ids vs
 * sheet canon vs RU labels) diff correctly. Used by the token→sheet condition
 * bridge (batch D): the ConditionsDialog produces a full desired set, but
 * `sheet:condition-toggle` is a per-condition toggle, so we send exactly the
 * canonical keys whose membership flipped.
 */
export function diffConditions(current, next) {
    const cur = new Set(current.map(normalizeConditionName));
    const nx = new Set(next.map(normalizeConditionName));
    const added = [...nx].filter(c => !cur.has(c));
    const removed = [...cur].filter(c => !nx.has(c));
    return [...added, ...removed];
}
/**
 * Canonical condition keys for pickers (Capitalized-English, no RU aliases).
 * `Dead` is excluded — death is driven by the death-save automation, not by
 * hand-picking a condition from a dropdown.
 */
export const DND5E_CANONICAL_CONDITIONS = CANONICAL_CONDITION_KEYS
    .filter((k) => k !== 'Dead');
/**
 * Round-count tracking for conditions ("stunned for 2 rounds").
 *
 * Stored as a JSON string in `sheet.values.condition_durations`
 * (values are primitives-only, same precedent as `action_economy`):
 * `{"Poisoned": 3}` — canonical condition key → remaining rounds.
 * A condition absent from the map is indefinite (until toggled off).
 * The countdown is decremented by ConditionDurationSubscriber at the start
 * of the bearer's initiative turn; at 0 the condition is auto-removed.
 */
export function parseConditionDurations(raw) {
    if (typeof raw !== 'string' || raw.length === 0)
        return {};
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
            return {};
        const out = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
                out[normalizeConditionName(k)] = Math.floor(v);
            }
        }
        return out;
    }
    catch {
        return {};
    }
}
/** Serialize back; empty map → empty string (callers may drop the key). */
export function serializeConditionDurations(map) {
    const entries = Object.entries(map).filter(([, v]) => v > 0);
    return entries.length === 0 ? '' : JSON.stringify(Object.fromEntries(entries));
}
